import { agentClient, type Product } from '@/services/agent-client'
import { offlineQueue } from '@/services/offline-queue'
import { APP } from '@/lib/constants'

// ---------------------------------------------------------------------------
// Product Cache
// ---------------------------------------------------------------------------

class ProductCache {
  private products: Record<string, Product> | null = null
  private lastRefresh = 0
  private refreshTimer: ReturnType<typeof setInterval> | null = null

  // ---- public API ---------------------------------------------------------

  async getProducts(forceRefresh?: boolean): Promise<Record<string, Product>> {
    const now = Date.now()
    const cacheValid = this.products !== null && now - this.lastRefresh < APP.PRODUCT_REFRESH_INTERVAL

    if (cacheValid && !forceRefresh) {
      return this.products!
    }

    try {
      const products = await agentClient.getProducts()
      this.products = products
      this.lastRefresh = Date.now()

      // Persist to IndexedDB for offline fallback
      offlineQueue.cacheProducts(products).catch(() => {})

      return products
    } catch {
      // Network error: fall back to IndexedDB cache
      const cached = await offlineQueue.getCachedProducts()
      if (cached) {
        this.products = cached
        return cached
      }

      // No cache available at all
      if (this.products) return this.products
      throw new Error('No products available (network error, no cache)')
    }
  }

  // ---- auto refresh -------------------------------------------------------

  startAutoRefresh(): void {
    this.stopAutoRefresh()
    this.refreshTimer = setInterval(() => {
      this.getProducts(true).catch(() => {})
    }, APP.PRODUCT_REFRESH_INTERVAL)
  }

  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // ---- cache control ------------------------------------------------------

  invalidate(): void {
    this.lastRefresh = 0
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const productCache = new ProductCache()
